import type {
	CourseMindItemSearchResult,
	DefaultMindItemSearchResult,
	MindItemSearchResult,
	ProductSearchResult,
} from '@kimuno/api-sdk';
import { createSelector } from 'reselect';

import { AsyncActionStatus } from '../actions/common.actionTypes';
import type { StoreType } from '.';

export const getPaginationSelector = (elements: SearchItems) => (store: StoreType) => {
	return store.search[elements].pagination;
};

export const getUnifiedPaginationSelector = (store: StoreType) => {
	return store.search.unified.pagination;
};

export enum SearchLoadingStatus {
	NONE = 'none',
	INITIAL = 'initial',
	PAGINATION = 'pagination',
	LOADED = 'loaded',
}

type SearchItems = 'products' | 'mindItems' | 'videoMindItems' | 'courseMindItems';

export const getSubTopics = (state: StoreType) => {
	return state.search.subTopics.terms;
};

export const getSubTopicLoadingStatus = (state: StoreType) => state.search.subTopics.status;

export const selectWorkloads = (state: StoreType) => state.search.workloads.data;

export const selectWorkloadLoadingStatus = (state: StoreType) => state.search.workloads.status;

export const selectTeachingFormats = (state: StoreType) => state.search.teachingFormats.data;

export const selectTeachingFormatLoadingStatus = (state: StoreType) =>
	state.search.teachingFormats.status;

export const getProductLoadingState = (state: StoreType) => {
	const pages = state.search.products.pages;
	if (pages.length === 0) {
		return SearchLoadingStatus.NONE;
	}
	if (pages[0].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.INITIAL;
	}
	if (pages[pages.length - 1].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.PAGINATION;
	}
	return SearchLoadingStatus.LOADED;
};

export const getMindItemLoadingState = (state: StoreType) => {
	const pages = state.search.mindItems.pages;
	if (pages.length === 0) {
		return SearchLoadingStatus.NONE;
	}
	if (pages[0].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.INITIAL;
	}
	if (pages[pages.length - 1].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.PAGINATION;
	}
	return SearchLoadingStatus.LOADED;
};

export const getUnifiedLoadingState = (state: StoreType) => {
	const pages = state.search.unified.pages;
	if (pages.length === 0) {
		return SearchLoadingStatus.NONE;
	}
	if (pages[0].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.INITIAL;
	}
	if (pages[pages.length - 1].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.PAGINATION;
	}
	return SearchLoadingStatus.LOADED;
};

export const getVideoMindItemsLoadingState = (state: StoreType) => {
	const pages = state.search.videoMindItems.pages;
	if (pages.length === 0) {
		return SearchLoadingStatus.NONE;
	}
	if (pages[0].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.INITIAL;
	}
	if (pages[pages.length - 1].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.PAGINATION;
	}
	return SearchLoadingStatus.LOADED;
};

export const getCourseMindItemsLoadingState = (state: StoreType) => {
	const pages = state.search.courseMindItems.pages;
	if (pages.length === 0) {
		return SearchLoadingStatus.NONE;
	}
	if (pages[0].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.INITIAL;
	}
	if (pages[pages.length - 1].status === AsyncActionStatus.LOADING) {
		return SearchLoadingStatus.PAGINATION;
	}
	return SearchLoadingStatus.LOADED;
};

type SearchReturnType<T> = T extends 'products'
	? ProductSearchResult
	: T extends 'courseMindItems'
		? CourseMindItemSearchResult
		: DefaultMindItemSearchResult;

const createSearchResultsSelector = <T extends SearchItems>(type: T) =>
	createSelector(
		[(state: StoreType) => state.search[type].pages, (state: StoreType) => state.search[type].byId],
		(pages, byId) =>
			pages.reduce((searchResults, page) => {
				const pageResults = page.data && page.data.map((id) => byId[id]);

				if (!pageResults) {
					return searchResults;
				}

				return searchResults.concat(pageResults as SearchReturnType<T>[]);
			}, [] as SearchReturnType<T>[])
	);

// One memoized selector per tab: reselect caches a single input/output pair, so
// the tabs would evict each other's result if they shared one.
const searchResultsSelectors: {
	[K in SearchItems]: (state: StoreType) => SearchReturnType<K>[];
} = {
	products: createSearchResultsSelector('products'),
	mindItems: createSearchResultsSelector('mindItems'),
	videoMindItems: createSearchResultsSelector('videoMindItems'),
	courseMindItems: createSearchResultsSelector('courseMindItems'),
};

export const getSearchResults = <T extends SearchItems>(
	state: StoreType,
	type: T
): SearchReturnType<T>[] => searchResultsSelectors[type](state);

/**
 * How many results the user can actually see for `type` — derived from the same
 * list the tabs render their empty state from, so the two cannot drift.
 *
 * `undefined` when the first page did not load successfully. An errored request
 * has no items either, but it is not a zero-result search, and callers must be
 * able to tell the two apart.
 */
export const getDisplayedResultCount = <T extends SearchItems>(
	state: StoreType,
	type: T
): number | undefined => {
	if (state.search[type].pages[0]?.status !== AsyncActionStatus.READY) {
		return undefined;
	}

	return getSearchResults(state, type).length;
};

export const getUnifiedSearchResults = createSelector(
	[
		(state: StoreType) => state.search.unified.pages,
		(state: StoreType) => state.search.unified.byId,
	],
	(pages, byId): (ProductSearchResult | MindItemSearchResult)[] =>
		pages.reduce(
			(searchResults, page) => {
				const pageResults = page.data && page.data.map((id) => byId[id]);

				if (!pageResults) {
					return searchResults;
				}

				return searchResults.concat(pageResults);
			},
			[] as (ProductSearchResult | MindItemSearchResult)[]
		)
);

export const selectSearchFilters = (state: StoreType) => state.search.searchFilters;
export const selectCourseSearchFilters = (state: StoreType) => state.search.courseSearchFilters;
export const selectResultsSearchId = (state: StoreType) => state.search.resultsSearchId;
export const selectCourseResultsSearchId = (state: StoreType) => state.search.courseResultsSearchId;
